// 📁 src/layouts/Category/data/CategoryManagerTable.js
import { useState } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import Table from "examples/Tables/Table";
import { fetchCategoryToggleStatus, fetchCategoryDelete } from "api/categoryService";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import AddCategory from "./AddCategory";
import DetailCategory from "./DetailCategory";

function CategoryManagerTable({
  data,
  totalElements,
  curPage,
  setCurPage,
  rowsCount,
  setRowsCount,
  refreshDataAfterChange,
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();

  const handleRowClick = (item) => {
    navigate("/Category/detail", { state: { selectedCategory: item } });
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsAddOpen(true);
  };

  const handleToggleStatus = async (item) => {
    const result = await fetchCategoryToggleStatus(item.categoryId);
    if (result.success) {
      Swal.fire({
        icon: "success",
        title: "Cập nhật trạng thái thành công",
        showConfirmButton: false,
        timer: 1500,
      });
      refreshDataAfterChange();
    } else {
      Swal.fire({
        icon: "error",
        title: "Thất bại",
        text: result.data?.message || "Không thể cập nhật trạng thái",
      });
    }
  };

  const handleDeleteCategory = async (item) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Bạn chắc chắn muốn xoá?",
      text: `Danh mục "${item.categoryName}" sẽ bị xoá!`,
      showCancelButton: true,
      confirmButtonText: "Xoá",
      cancelButtonText: "Huỷ",
    });

    if (confirm.isConfirmed) {
      const result = await fetchCategoryDelete(item.categoryId);
      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Xoá thành công!",
          showConfirmButton: false,
          timer: 1500,
        });
        refreshDataAfterChange();
      } else {
        Swal.fire({
          icon: "error",
          title: "Lỗi",
          text: result.data?.message || "Không thể xoá danh mục",
        });
      }
    }
  };

  const columns = [
    { name: "Tên danh mục", align: "center", alignHeader: "center", sortable: true },
    { name: "Ngày tạo", align: "center", alignHeader: "center", sortable: true },
    { name: "Số lượng sản phẩm", align: "center", alignHeader: "center", sortable: true },
    { name: "Sắp xếp thứ tự hiển thị", align: "center", alignHeader: "center", sortable: true },
    { name: "Chức năng", align: "center", alignHeader: "center" },
  ];

  const rows = data.map((item) => ({
    "Tên danh mục": item.categoryName || "---",
    "Ngày tạo": moment(item.createTime).format("DD/MM/YYYY"),
    "Số lượng sản phẩm": item.totalProducts || 0,
    "Sắp xếp thứ tự hiển thị": (
      <SoftBox
        sx={{
          backgroundColor: "#F59F00",
          color: "white !important",
          fontWeight: "bold",
          borderRadius: "8px",
          padding: "2px 10px",
          display: "inline-block",
          textAlign: "center",
          minWidth: "24px",
        }}
      >
        {item.displayOrder || "-"}
      </SoftBox>
    ),
    "Chức năng": (
      <SoftBox display="flex" justifyContent="center" gap={1}>
        <Switch
          title={item.status === 1 ? "Kích hoạt" : "Tắt"}
          checked={item.status === 1}
          onClick={(e) => e.stopPropagation()}
          onChange={() => handleToggleStatus(item)}
        />
        <IconButton
          sx={{
            color: "red",
          }}
          title="Xóa"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteCategory(item);
          }}
        >
          <DeleteIcon />
        </IconButton>

        <IconButton
          sx={{
            color: "green",
          }}
          title="Sửa"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(item);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </SoftBox>
    ),
    onClick: () => handleRowClick(item),
    sx: { cursor: "pointer !important" },
  }));

  return (
    <SoftBox>
      <Table
        columns={columns}
        rows={rows}
        isPagination={true}
        totalElements={totalElements}
        rowsPerPageOptions={[5, 10, 20, 50]}
        rowsCount={rowsCount}
        setRowsCount={setRowsCount}
        curPage={curPage}
        setCurPage={setCurPage}
        isRowSelectable={true}
        isDisabledSingleRow={() => false}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRowClick={(row) => row.onClick?.()}
      />

      <DetailCategory
        open={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        selectedCategory={selectedItem}
      />

      <AddCategory
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        editMode={!!selectedItem}
        initialData={selectedItem}
        refreshDataAfterChange={refreshDataAfterChange}
      />
    </SoftBox>
  );
}

CategoryManagerTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  totalElements: PropTypes.number.isRequired,
  curPage: PropTypes.number.isRequired,
  setCurPage: PropTypes.func.isRequired,
  rowsCount: PropTypes.number.isRequired,
  setRowsCount: PropTypes.func.isRequired,
  refreshDataAfterChange: PropTypes.func.isRequired,
};

export default CategoryManagerTable;
